import { z } from "zod";

// --- SSO Group Mapping ---
export const SSOGroupMappingResponseSchema = z.object({
  id: z.number(),
  uuid: z.string(),
  external_group_identifier: z.string(),
  match_type: z.string(),
  mapping_type: z.string(),
  target_id: z.number(),
  target_name: z.string().nullable().optional(),
  is_active: z.boolean(),
  created_by_email: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type SSOGroupMappingResponse = z.infer<typeof SSOGroupMappingResponseSchema>;

export const SSOGroupMappingListSchema = z.array(SSOGroupMappingResponseSchema);

// --- SSO Team Sync Config ---
export const SSOTeamSyncConfigResponseSchema = z.object({
  enabled: z.boolean(),
  provider: z.string().nullable().optional(),
  group_claim_name: z.string(),
  group_prefix_filter: z.string().nullable().optional(),
  enable_graph_api_fallback: z.boolean(),
  graph_api_tenant_id: z.string().nullable().optional(),
  enable_okta_api_fallback: z.boolean(),
  okta_domain: z.string().nullable().optional(),
});

export type SSOTeamSyncConfigResponse = z.infer<typeof SSOTeamSyncConfigResponseSchema>;

// --- Preview ---
export const PreviewTeamMatchSchema = z.object({
  user_group_id: z.number(),
  source_group: z.string(),
});

export const PreviewRoleMatchSchema = z.object({
  role: z.string(),
  source_group: z.string(),
});

export const SSOPreviewResponseSchema = z.object({
  team_results: z.array(PreviewTeamMatchSchema),
  role_result: PreviewRoleMatchSchema.nullable().optional(),
  matched_teams: z.number(),
  unmatched_count: z.number(),
});

export type SSOPreviewResponse = z.infer<typeof SSOPreviewResponseSchema>;

// --- Audit Events ---
export const SSOAuditEventResponseSchema = z.object({
  id: z.number(),
  tenant: z.string(),
  user_id: z.number().nullable().optional(),
  user_email: z.string().nullable().optional(),
  event_type: z.string(),
  target_type: z.string().nullable().optional(),
  target_id: z.number().nullable().optional(),
  target_name: z.string().nullable().optional(),
  old_value: z.string().nullable().optional(),
  new_value: z.string().nullable().optional(),
  source: z.string(),
  source_group_identifier: z.string().nullable().optional(),
  performed_by: z.number().nullable().optional(),
  performed_by_email: z.string().nullable().optional(),
  created_at: z.string(),
});

export type SSOAuditEventResponse = z.infer<typeof SSOAuditEventResponseSchema>;

export const SSOAuditEventListResponseSchema = z.object({
  events: z.array(SSOAuditEventResponseSchema),
  total: z.number(),
});

export type SSOAuditEventListResponse = z.infer<typeof SSOAuditEventListResponseSchema>;

// --- Mapping Targets (teams + roles for dropdowns) ---
export const SSOTargetsResponseSchema = z.object({
  teams: z.array(z.object({ id: z.number(), name: z.string() })),
  roles: z.array(z.object({ id: z.number(), role_title: z.string() })),
});

export type SSOTargetsResponse = z.infer<typeof SSOTargetsResponseSchema>;

// --- External Groups (CSV upload) ---
export const SSOExternalGroupSchema = z.object({
  id: z.number(),
  group_name: z.string(),
  source: z.string(),
  created_at: z.string(),
});

export type SSOExternalGroup = z.infer<typeof SSOExternalGroupSchema>;

export const SSOExternalGroupListResponseSchema = z.object({
  groups: z.array(SSOExternalGroupSchema),
  total: z.number(),
});

// --- Bulk Upload ---
export const BulkUploadFailureSchema = z.object({
  row: z.number(),
  reason: z.string(),
});

export const SSOBulkUploadResponseSchema = z.object({
  total_processed: z.number(),
  created: z.number(),
  skipped: z.number(),
  reactivated: z.number(),
  failures: z.array(BulkUploadFailureSchema),
});

export type SSOBulkUploadResponse = z.infer<typeof SSOBulkUploadResponseSchema>;

export const SSOBulkMappingUploadResponseSchema = z.object({
  total_processed: z.number(),
  created: z.number(),
  failures: z.array(BulkUploadFailureSchema),
});

export type SSOBulkMappingUploadResponse = z.infer<typeof SSOBulkMappingUploadResponseSchema>;

// --- Request body types ---
export interface SSOTeamSyncConfigUpdatePayload {
  enabled?: boolean;
  provider?: string | null;
  group_claim_name?: string | null;
  group_prefix_filter?: string | null;
  enable_graph_api_fallback?: boolean;
  graph_api_tenant_id?: string | null;
  enable_okta_api_fallback?: boolean;
  okta_domain?: string | null;
}

export interface SSOGroupMappingUpdatePayload {
  external_group_identifier: string;
  match_type: string;
  target_id: number;
}

// --- Enum types for forms ---
export const MATCH_TYPES = [
  { value: "exact", label: "Exact Match" },
  { value: "prefix", label: "Starts With" },
  { value: "contains", label: "Contains" },
  { value: "suffix", label: "Ends With" },
] as const;

export const MAPPING_TYPES = [
  { value: "team", label: "Team" },
  { value: "role", label: "Role" },
] as const;

export const EVENT_TYPES = [
  { value: "team_added", label: "Team Added" },
  { value: "team_removed", label: "Team Removed" },
  { value: "team_skip_manual", label: "Team Skip (Manual)" },
  { value: "role_changed", label: "Role Changed" },
  { value: "role_skip_manual", label: "Role Skip (Manual)" },
  { value: "mapping_created", label: "Mapping Created" },
  { value: "mapping_deleted", label: "Mapping Deleted" },
  { value: "mapping_updated", label: "Mapping Updated" },
  { value: "manual_role_override", label: "Manual Role Override" },
  { value: "manual_team_override", label: "Manual Team Override" },
  { value: "user_deactivated", label: "User Deactivated" },
  { value: "user_reactivated", label: "User Reactivated" },
  { value: "role_conflict_detected", label: "Role Conflict Detected" },
  { value: "role_conflict_resolved", label: "Role Conflict Resolved" },
  { value: "role_conflict_dismissed", label: "Role Conflict Dismissed" },
] as const;
